import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import {
  ArrowBack,
  Clear as ClearIcon,
  PaymentsOutlined,
  Search as SearchIcon,
} from "@mui/icons-material";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import { borderRadius, colorsFoundation } from "@accredo/fe-shared/theme/index.js";
import typography from "@accredo/fe-shared/theme/tokens/typography.js";
import { ListContainer, ListItem } from "./lists.jsx";
import { getDatumZeit, getEuro } from "@accredo/fe-shared/services/formatService.js";

const PaymentMatchingClaimList = ({
  claimsSearchword,
  onClaimsSearchwordChange,
  claimsListRef,
  onClaimsScroll,
  loadingClaims,
  sortedFilteredClaims,
  matchPayments,
  selectedPayment,
  selectedClaim,
  onSelectClaim,
  onBackToPayments,
  loadingMoreClaims,
  hasMoreClaims,
}) => {
  if (!selectedPayment) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: borderRadius.md,
          border: `1px solid ${colorsFoundation.neutral[200]}`,
          minHeight: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="h6" sx={{ fontSize: typography.size.xl, mb: 2 }}>
          Schäden
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "text.secondary",
          }}
        >
          <PaymentsOutlined
            fontSize="large"
            sx={{ mb: 2, color: colorsFoundation.neutral[300] }}
          />
          <Typography variant="body1" color={colorsFoundation.neutral[700]}>
            Bitte wählen Sie zuerst eine Zahlung aus
          </Typography>
        </Box>
      </Paper>
    );
  }

  const renderClaimContent = (claim) => {
    const matches = matchPayments(claim);

    return (
      <ListItem
        key={`claim_${claim.id}`}
        selected={selectedClaim?.id === claim.id}
        onClick={() => onSelectClaim(claim)}
        showSelectionIndicator
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {claim.schaden_nummer_accredo}
            </Typography>
            {!!claim.schaden_nummer_vu && (
              <Typography variant="body2">
                VU: {claim.schaden_nummer_vu}
              </Typography>
            )}
            <Typography variant="body2">
              VN: {claim?.vertrag?.vertragsnummer}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {claim?.vertrag?.gesellschaft?.name}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Datum:
                <br />
                {claim.schaden_datum ? getDatumZeit(claim.schaden_datum) : "-"}
              </Typography>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary">
                  {claim.betrag_label}:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                  }}
                >
                  {getEuro(claim.betrag)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {matches.total ? (
            <Box
              component="span"
              sx={{
                px: 1,
                py: 0.25,
                height: "fit-content",
                borderRadius: 999,
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
                backgroundColor:
                  matches.exact.length > 0
                    ? colorsFoundation.key.blue[700]
                    : colorsFoundation.status.critical[400],
                color:
                  matches.exact.length > 0
                    ? colorsFoundation.neutral.white
                    : colorsFoundation.neutral[900],
              }}
            >
              {matches.exact.length > 0 ? "Exakt" : "Möglich"}
            </Box>
          ) : null}
        </Box>
      </ListItem>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: borderRadius.md,
        border: `1px solid ${colorsFoundation.neutral[200]}`,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Button
          size="small"
          startIcon={<ArrowBack fontSize="small" />}
          onClick={onBackToPayments}
          sx={{ pl: 0, textTransform: "none" }}
        >
          Andere Zahlung wählen
        </Button>
      </Box>
      <Typography variant="h6" sx={{ fontSize: typography.size.xl, mb: 2 }}>
        Schäden
      </Typography>
      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          id="payment-matching-claim-search"
          placeholder="Schäden suchen..."
          value={claimsSearchword}
          onChange={(e) => onClaimsSearchwordChange(e.target.value)}
          fullWidth
          size="small"
          label="Schaden suchen"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  {claimsSearchword ? (
                    <IconButton
                      size="small"
                      aria-label="Suche zurücksetzen"
                      onClick={() => onClaimsSearchwordChange("")}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <SearchIcon fontSize="small" />
                  )}
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <Box
        ref={claimsListRef}
        onScroll={onClaimsScroll}
        sx={{ flex: 1, overflow: "auto", mt: 1 }}
      >
        {loadingClaims ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Spinner size={24} />
          </Box>
        ) : (
          <ListContainer>
            {sortedFilteredClaims.map((claim) => renderClaimContent(claim))}

            {!loadingMoreClaims && sortedFilteredClaims.length === 0 && (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Keine Schäden gefunden
                </Typography>
              </Box>
            )}

            {loadingMoreClaims && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 2,
                }}
              >
                <Spinner size={20} />
              </Box>
            )}

            {!hasMoreClaims && sortedFilteredClaims.length > 0 && (
              <Box sx={{ py: 2, textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  Alle Schäden geladen
                </Typography>
              </Box>
            )}
          </ListContainer>
        )}
      </Box>
    </Paper>
  );
};

PaymentMatchingClaimList.propTypes = {
  claimsSearchword: PropTypes.string.isRequired,
  onClaimsSearchwordChange: PropTypes.func.isRequired,
  claimsListRef: PropTypes.shape({ current: PropTypes.any }),
  onClaimsScroll: PropTypes.func.isRequired,
  loadingClaims: PropTypes.bool.isRequired,
  sortedFilteredClaims: PropTypes.arrayOf(PropTypes.object).isRequired,
  matchPayments: PropTypes.func.isRequired,
  selectedPayment: PropTypes.object,
  selectedClaim: PropTypes.object,
  onSelectClaim: PropTypes.func.isRequired,
  onBackToPayments: PropTypes.func.isRequired,
  loadingMoreClaims: PropTypes.bool.isRequired,
  hasMoreClaims: PropTypes.bool.isRequired,
};

export default PaymentMatchingClaimList;
